import type { CookieSerializeOptions } from 'cookie-es'
import type { Location } from '@/models'

export const useLocationStore = defineStore('location.store', () => {
  const nuxtApp = useNuxtApp()

  const latitude = ref<number>(0)
  const longitude = ref<number>(0)
  const cachedHost = ref('')
  const locationList = ref<Location[]>([])
  const locationListGrouped =
    ref<Undefinable<Dictionary<Location[]>>>(undefined)
  const currentLocation = ref<Undefinable<Location>>(undefined)
  const errors = ref<string[]>([])
  const fixedHostMode = ref(false)
  const fixedHost = ref('')
  const locationPrefix = computed(() => {
    return currentLocation.value && currentLocation.value.domain.split('.')[0]
  })

  const cookieStore = useCookiesStore()
  const cookieParams = computed(
    (): CookieSerializeOptions => ({
      sameSite: 'none',
      domain: cookieStore.cookiesDomain,
      path: '/',
      secure: true,
      httpOnly: false,
    }),
  )

  const setFixedHost = (host: string) => {
    fixedHost.value = host
  }
  const setFixedHostMode = (value: boolean) => {
    fixedHostMode.value = value
  }

  const setLocation = ({
    latitudeData,
    longitudeData,
  }: {
    latitudeData: number
    longitudeData: number
  }) => {
    latitude.value = latitudeData
    longitude.value = longitudeData
  }

  const getLocation = async () => {
    try {
      const cachedLocation = useCookie('cached_location')

      if (cachedLocation.value) return cachedLocation.value
      const { data } = await nuxtApp.$api.resources.geoLocation.getLocation()

      return data.domain.split('.')[0]
    } catch (error) {
      errors.value.push(StringifyError(error))
    }
  }

  const getLocationList = async () => {
    try {
      const locationListData = await nuxtApp.$api.resources.location.getAll()

      if (locationListData && locationListData.data) {
        locationList.value = locationListData.data
      }
    } catch (error) {
      errors.value.push(StringifyError(error))
    }
  }

  const setCurrentLocation = (hostLocation = '') => {
    const cachedLocation = useCookie<string | undefined>(
      'cached_location',
      cookieParams.value,
    )

    if (!fixedHostMode.value) {
      cachedHost.value = hostLocation
      cachedLocation.value = hostLocation
    }

    currentLocation.value = locationList.value.find(
      (item) => item.domain.split('.')[0] === hostLocation,
    )
  }

  const getGroupLocationList = (list: Location[]) => {
    const collator = new Intl.Collator()
    const locations = list
      .filter((item) => item.isPublic)
      .sort((locationA, locationB) => {
        // не сортируем особые города, иначе москва будет под казанью
        if (locationA.isFeatured || locationB.isFeatured) return 0

        return collator.compare(locationA.name, locationB.name)
      })
      .reduce((list: Dictionary<Location[]>, item: Location) => {
        let group = item.name[0]

        if (item.isFeatured) group = ''

        list[group] = list[group] ?? []
        list[group].push(item)

        return list
      }, {})

    locationListGrouped.value = Object.keys(locations)
      .sort()
      .reduce((item: Dictionary<Location[]>, key: string) => {
        item[key] = locations[key]

        return item
      }, {})
  }

  const getHost = () => {
    const host = fixedHostMode.value
      ? fixedHost.value
      : process.server
      ? useRequestEvent()?.node.req.headers.host
      : window.location.host

    let hostLocation = host?.split('.')[0]

    if (
      (process.env.NODE_ENV === 'development' ||
        !hostLocation ||
        hostLocation.includes('localhost')) &&
      !fixedHostMode.value
    )
      hostLocation = 'msk'

    return hostLocation
  }

  const getCurrentLocation = () => {
    const cachedLocation = useCookie('cached_location')
    const host =
      (!fixedHostMode.value && (cachedHost.value || cachedLocation.value)) ||
      getHost()

    return locationList.value.filter(
      (item) => item.domain.split('.')[0] === host,
    )[0]
  }

  const getAbsoluteLink = (
    relativePath?: string,
    { withProto } = { withProto: false },
  ) => {
    if (!relativePath) return ''
    if (
      relativePath.startsWith('http') ||
      relativePath.startsWith('https') ||
      relativePath.startsWith('www')
    )
      return relativePath
    if (!currentLocation.value) return relativePath
    const domain = getCurrentLocation().domain
    const path = withoutPrefixSlash(relativePath)

    const proto = withProto ? 'https:' : ''

    return proto + withPrefixDoubleSlash([domain, path].join('/'))
  }

  return {
    latitude,
    longitude,
    locationList,
    locationListGrouped,
    currentLocation,
    errors,
    locationPrefix,
    setLocation,
    getLocation,
    getLocationList,
    setCurrentLocation,
    getGroupLocationList,
    getHost,
    getCurrentLocation,
    getAbsoluteLink,
    setFixedHost,
    setFixedHostMode,
  }
})
